import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { H2, H3, P, UL, Note, TBD, LEGAL_ENTITY, PRODUCT, CONTACT_EMAIL } from './LegalLayout';

// NOTE FOR FUTURE EDITORS AND FOR COUNSEL
// --------------------------------------------------------------------------
// This page is deliberately NOT a Notice of Privacy Practices.
//
// A Notice of Privacy Practices (45 C.F.R. § 164.520) is an obligation of a
// COVERED ENTITY — the medical practice. Adarga Health Technologies LLC is a BUSINESS
// ASSOCIATE. A business associate that publishes its own NPP misrepresents
// its role, and would mislead patients into believing they can exercise
// HIPAA rights (access, amendment, accounting of disclosures) against us
// rather than against their provider. We cannot grant those rights; only the
// practice can.
//
// So this page does the two things a business associate legitimately can:
//   1. explains our role plainly, in patient-readable language, and
//   2. routes patients to the party that actually owes them their rights.
//
// Separately, offering practices an NPP TEMPLATE they can adopt is a
// reasonable product feature. That is a different artifact from this page,
// and it must not be presented as our notice.
// --------------------------------------------------------------------------

export default function HipaaNotice() {
  return (
    <LegalLayout
      title="HIPAA & Your Health Information"
      subtitle={`How ${PRODUCT} handles health information, and where your rights come from.`}
      updated="October 22, 2026"
    >
      <Note>
        <strong>This is not a Notice of Privacy Practices.</strong> Your doctor's office issues that notice, because
        your doctor's office is the one legally responsible for your medical record. {LEGAL_ENTITY} is a vendor that
        handles your information <em>for</em> them, under contract. This page explains what we do and who to ask about
        your rights.
      </Note>

      <H2 id="plain">The short version</H2>
      <UL>
        <li>Your practice uses {PRODUCT} to fill out and sign your medical leave paperwork.</li>
        <li>We store your answers and the finished forms securely, and we only use them to prepare your paperwork.</li>
        <li>We never sell your information, and we never share it for advertising.</li>
        <li>Only your care team at your practice can see your information. We access it only to keep the service running or when your practice asks us to help.</li>
        <li>To see your records, correct them, or ask questions about your rights, contact your doctor's office.</li>
      </UL>

      <H2 id="roles">Who is responsible for what</H2>
      <P>
        HIPAA uses two terms that explain the whole arrangement.
      </P>
      <H3>Your medical practice is the "covered entity"</H3>
      <P>
        It holds your medical record, decides what goes in it, decides who it may be disclosed to, and owes you the
        rights HIPAA gives patients. It gave you — or must give you — a Notice of Privacy Practices describing all of
        this. If you do not have a copy, ask the front desk.
      </P>
      <H3>{LEGAL_ENTITY} is a "business associate"</H3>
      <P>
        We provide software to your practice. We handle your health information only because it is necessary to
        provide that software, and only in the ways our written Business Associate Agreement with the practice permits.
        That agreement legally binds us to protect your information, to use it for nothing else, to report any breach,
        and to return or destroy it when the practice's use of our service ends.
      </P>
      <P>
        We cannot decide to use your information for our own purposes. We cannot disclose it to anyone your practice
        has not authorized. And we cannot grant or deny your HIPAA rights — those run against the practice.
      </P>

      <H2 id="what">What information we handle, and why</H2>
      <P>When your practice sends you a secure link and you fill in the form, we receive:</P>
      <UL>
        <li>your name, date of birth, and contact details, so the form can be completed and so we can tell you when it is ready;</li>
        <li>your employer's name and, where the form requires it, their contact details;</li>
        <li>the health details the form asks for — your condition, treatment, limitations, and how long you expect to need leave;</li>
        <li>anything you upload, such as photographs of your employer's own form pages;</li>
        <li>your confirmation that you agree to share your answers with your care team.</li>
      </UL>
      <P>
        We use it for one purpose: preparing, reviewing, signing, and delivering your leave documents. Nothing else.
      </P>

      <H2 id="protect">How we protect it</H2>
      <UL>
        <li><strong>Encrypted.</strong> Your information is encrypted while travelling over the internet and while stored.</li>
        <li><strong>Separated by practice.</strong> Every request is checked so that one practice can never see another practice's patients.</li>
        <li><strong>Logged.</strong> Every time someone views, prepares, signs, or sends your document, it is recorded — who, what, and when. Your practice's administrator can review that log.</li>
        <li><strong>No health information in emails or texts.</strong> Our messages say a document is ready and give you a secure link. They never contain your diagnosis, your condition, or anything else about your health, so an email landing in the wrong inbox reveals nothing clinical.</li>
        <li><strong>Links that expire.</strong> Your personal link stops working after a set period, so an old message cannot be used to reach your information later.</li>
        <li><strong>Least information to the AI.</strong> See below.</li>
      </UL>

      <H2 id="ai">Where artificial intelligence fits in</H2>
      <P>
        {PRODUCT} uses AI to write a first draft of the narrative sections of your forms and to read the form pages you
        upload. A nurse and a physician review everything before anything is signed. The AI does not make medical
        decisions and it does not sign anything.
      </P>
      <P>
        <strong>The AI does not learn who you are.</strong> Before your information is sent to the AI, we remove the
        details that identify you — your name, your employer's name, your date of birth, your phone number and email,
        and even calendar dates, which we convert into lengths of time such as "six weeks". What the AI sees is
        clinical, not personal.
      </P>
      <P>
        The AI service we use is bound by a Business Associate Agreement with us, signed on July 30, 2026, and it is not
        permitted to use your information to train its models. Neither do we.
      </P>

      <H2 id="fax">One channel that does carry your document: fax</H2>
      <P>
        Some employers and insurers still require fax. Unlike email and text, a fax transmits your completed document
        itself. Because of that, faxing is switched off in our system unless a Business Associate Agreement with the
        fax provider is in place. Your practice chooses whether to use it.
      </P>

      <H2 id="rights">Your rights — and who to ask</H2>
      <P>
        HIPAA gives you the right to see and get a copy of your health information, to ask for corrections, to find out
        who it has been disclosed to, to ask for restrictions on how it is used, to ask for confidential
        communications, and to receive a Notice of Privacy Practices.
      </P>
      <Note>
        <strong>Direct all of these to your medical practice.</strong> They hold your record and they are the ones
        HIPAA obliges to respond. If you ask us, we will tell you to contact them and let them know you asked — we are
        not permitted to release your record on our own.
      </Note>
      <P>
        You can see the current status of your own paperwork at any time using the secure link your practice sent you.
      </P>

      <H3>If something has gone wrong</H3>
      <P>
        If you believe your privacy has been violated, you can complain to:
      </P>
      <UL>
        <li><strong>your medical practice</strong> — their Notice of Privacy Practices names the person to contact;</li>
        <li><strong>us</strong>, at <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>, if you think the problem is with our software; or</li>
        <li><strong>the U.S. Department of Health and Human Services, Office for Civil Rights</strong>, at{' '}
          <span className="font-medium text-foreground">hhs.gov/ocr/privacy/hipaa/complaints</span>.</li>
      </UL>
      <P>No one is allowed to retaliate against you for making a complaint.</P>

      <H2 id="breach">If information is ever exposed</H2>
      <P>
        If we discover that unsecured health information has been breached, we notify the affected practice without
        unreasonable delay and within the time HIPAA requires. The practice then notifies the patients affected,
        because as the covered entity that duty is theirs. We support them in doing it.
      </P>

      <H2 id="practices">For practices evaluating {PRODUCT}</H2>
      <P>
        We will execute a Business Associate Agreement before any real patient information enters the system —
        evaluation environments are loaded with synthetic data for exactly this reason. Our technical safeguards, our
        subprocessors and their BAA status, and our de-identification approach are documented in the{' '}
        <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy Policy</Link>, and the
        contractual terms are in the{' '}
        <Link to="/terms" className="font-medium text-primary hover:underline">Terms of Service</Link>. Our standard{' '}
        <Link to="/baa" className="font-medium text-primary hover:underline">Business Associate Agreement</Link> is
        published in full. For a countersigned copy, security documentation, or the results of our most recent risk
        assessment, contact{' '}
        <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>.
      </P>

      <H2 id="contact">Contact</H2>
      <P>
        {LEGAL_ENTITY} · 530 Berwick Town, San Antonio, TX 78249
        <br />
        <span className="font-medium text-foreground">{CONTACT_EMAIL}</span>
        <br />
        HIPAA contact: Colin Harris, Privacy and Security Officer
      </P>
    </LegalLayout>
  );
}
